import { Empty, formatUtc } from '../../components/Async'
import { BarChart } from '../../components/charts/BarChart'
import { Donut } from '../../components/charts/Donut'
import { LineChart } from '../../components/charts/LineChart'
import { Sparkline } from '../../components/charts/Sparkline'
import { StackedBarChart } from '../../components/charts/StackedBarChart'
import { StatTiles } from '../../components/charts/StatTiles'
import { exactNumber, shortDay } from '../../components/charts/scale'
import type {
  DestinationCount,
  FleetCoverage,
  DetectionTrendRow,
  DeviceActivityRow,
  FleetTopology,
  ProjectionState
} from '../../lib/api'
import type { ChartKind, WidgetDefinition } from '../../lib/preferences'
import { busiestDevices, coverage, days, noisiestRules, severityMix, sumByDay, trendFor } from './aggregate'
import { fleetSummary } from './fleetSummary'
import { ribbonTiles } from './ribbon'

/** Everything the dashboard has loaded, handed to whichever widgets are switched on. */
export interface DashboardData {
  /** Null when the read did not arrive; an empty array when it arrived and found nothing (D-263). */
  activity: DeviceActivityRow[] | null
  detections: DetectionTrendRow[] | null
  topology: FleetTopology | null
  /** Null when the read did not arrive; an empty array when it arrived and found nothing (D-263). */
  coverage: FleetCoverage | null
  destinations: DestinationCount[] | null
  openCases: number | null
  projections: ProjectionState[]
  windowDays: number
  busy: boolean
  rebuildTopology: () => void
  rebuild: (projection: string) => void
}

export interface DashboardWidget extends WidgetDefinition {
  title: string
  description: string
  render: (chart: ChartKind, data: DashboardData) => React.ReactNode
}

const REBUILDABLE = ['device_activity', 'detection_trend']

/**
 * What a widget says when its read model did not arrive.
 *
 * Distinct from "nothing in this period" on purpose. Drawing an empty chart for a failed load is the
 * same error the tiles used to make, one panel down: it reports an absence of data as an absence of
 * activity, and those are different facts (D-263).
 */
const UNREADABLE = 'This could not be loaded. Reload the page, or sign in again if your session has expired.'

/**
 * Every widget the dashboard can draw.
 *
 * A list rather than a page of hard-coded cards, because the arrangement is now the operator's: they
 * choose which of these appear, in what order, and which of the forms each is drawn in. The catalog
 * is the browser's alone — the server stores the choices without knowing what a widget is, so adding
 * one here is the whole change, with no migration and no deployment ordering to think about.
 *
 * `defaultVisible` is what somebody sees before they have chosen anything, and it is deliberately not
 * everything: a first dashboard that shows nine panels is one nobody reads. The rest are a checkbox
 * away.
 */
export const WIDGETS: DashboardWidget[] = [
  {
    // The Board's ribbon: what is outstanding, above what has been happening. First in the catalog
    // and pinned, because a board whose first panel is a chart is a dashboard again.
    id: 'board-ribbon',
    title: 'What needs a decision',
    description: 'Three things that are outstanding, and three that are merely true.',
    charts: ['tiles'],
    defaultChart: 'tiles',
    defaultSize: 'full',
    sizes: ['full'],
    defaultVisible: true,
    pinned: true,
    // Layout only. Which figure is a problem, which is context, and - the part that matters - which
    // is a dash because nobody measured it, are all decided in ribbon.ts (D-263).
    render: (_chart, data) => (
      <StatTiles
        stats={ribbonTiles({
          coverage: data.coverage,
          activity: data.activity,
          openCases: data.openCases,
          windowDays: data.windowDays
        })}
      />
    )
  },
  {
    // Where the fleet's detections were going. `agent` has been filterable since Phase 5 and nothing
    // has ever grouped by it, so an operator could ask about one assistant and not about all of them.
    id: 'detections-by-destination',
    title: 'Where it was going',
    description: 'Which assistant the fleet’s detections were headed for.',
    charts: ['bar', 'donut', 'table'],
    defaultChart: 'bar',
    defaultSize: 'half',
    defaultVisible: true,
    render: (chart, data) => {
      if (data.destinations === null) return <Empty>{UNREADABLE}</Empty>
      if (data.destinations.length === 0) return <Empty>No detections in this period.</Empty>

      const ranked = data.destinations.slice(0, 8)
      const labels = ranked.map((entry) => entry.agentId ?? 'could not be attributed')

      if (chart === 'donut') {
        return (
          <Donut
            title="Where it was going"
            description="Detections in this period, by the assistant they were headed for. The whole is every detection the fleet sent."
            slices={ranked.map((entry, index) => ({ label: labels[index]!, value: entry.detections }))}
            totalLabel="detections"
          />
        )
      }

      return chart === 'table' ? (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Assistant</th>
                <th>Detections</th>
                <th className="secondary">Devices</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((entry, index) => (
                <tr key={index}>
                  <td>{labels[index]}</td>
                  <td>{exactNumber(entry.detections)}</td>
                  <td className="secondary">{exactNumber(entry.devices)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <BarChart
          title="Where it was going"
          description="Detections in this period, by the assistant they were headed for."
          categories={labels}
          categoryHeading="Assistant"
          series={{ key: 'detections', label: 'Detections', values: ranked.map((entry) => entry.detections) }}
        />
      )
    }
  },
  {
    id: 'fleet-summary',
    title: 'Fleet at a glance',
    description: 'How many machines there are, and how many of them nothing is watching.',
    charts: ['tiles'],
    defaultChart: 'tiles',
    defaultSize: 'full',
    defaultVisible: true,
    // Layout only. What each tile says — and, more to the point, when it says nothing — is decided in
    // fleetSummary.ts, because this widget is the one that printed an all-clear whenever a read failed
    // and there was no way to test it in place.
    render: (_chart, data) => (
      <StatTiles
        stats={fleetSummary({
          activity: data.activity,
          topology: data.topology,
          windowDays: data.windowDays
        })}
      />
    )
  },

  {
    id: 'event-volume',
    title: 'What the fleet reported',
    description: 'Events each day, and how much of that was a finding or a failure.',
    charts: ['area', 'line', 'stacked'],
    defaultChart: 'area',
    defaultSize: 'full',
    defaultVisible: true,
    render: (chart, data) => {
      if (data.activity === null) return <Empty>{UNREADABLE}</Empty>
      const axis = days(data.activity)
      if (axis.length === 0) return <Empty>No activity in this period.</Empty>

      const series = [
        { key: 'events', label: 'Events', values: sumByDay(data.activity, axis, (row) => row.eventCount) },
        { key: 'findings', label: 'Findings', values: sumByDay(data.activity, axis, (row) => row.findingCount) },
        { key: 'failures', label: 'Failures', values: sumByDay(data.activity, axis, (row) => row.failureCount) }
      ]

      const common = {
        title: 'What the fleet reported',
        description:
          'Daily totals across every device that checked in. A failure is a capability that could not run, which is a different problem from one that ran and found nothing.',
        categories: axis,
        labels: axis.map(shortDay),
        series
      }

      return chart === 'stacked' ? <StackedBarChart {...common} /> : <LineChart {...common} filled={chart === 'area'} />
    }
  },

  {
    id: 'detection-trend',
    title: 'Detections over time',
    description: 'How much matched each day, and on how many machines.',
    charts: ['line', 'area', 'stacked'],
    defaultChart: 'line',
    defaultSize: 'full',
    defaultVisible: true,
    render: (chart, data) => {
      if (data.detections === null) return <Empty>{UNREADABLE}</Empty>
      const axis = days(data.detections)
      if (axis.length === 0) return <Empty>Nothing matched in this period.</Empty>

      const series = [
        { key: 'matches', label: 'Matches', values: sumByDay(data.detections, axis, (row) => row.matchCount) },
        { key: 'devices', label: 'Devices affected', values: sumByDay(data.detections, axis, (row) => row.deviceCount) }
      ]

      const common = {
        title: 'Detections over time',
        description:
          'A rule firing twice a day on one machine and twice a day across two hundred are different rules, so both counts are drawn.',
        categories: axis,
        labels: axis.map(shortDay),
        series
      }

      return chart === 'stacked' ? <StackedBarChart {...common} /> : <LineChart {...common} filled={chart === 'area'} />
    }
  },

  {
    id: 'busiest-devices',
    title: 'Busiest devices',
    description: 'The machines producing the most events.',
    charts: ['bar', 'donut', 'table'],
    defaultChart: 'bar',
    defaultSize: 'half',
    defaultVisible: true,
    render: (chart, data) => {
      if (data.activity === null) return <Empty>{UNREADABLE}</Empty>
      const ranked = busiestDevices(data.activity)
      if (ranked.length === 0) return <Empty>No activity in this period.</Empty>

      if (chart === 'donut') {
        return (
          <Donut
            title="Busiest devices"
            description="Events produced across the whole period. The whole is what these devices produced between them, not what the fleet did."
            slices={ranked.map((entry) => ({ label: entry.key, value: entry.value }))}
            totalLabel="events"
          />
        )
      }

      if (chart === 'bar') {
        return (
          <BarChart
            title="Busiest devices"
            description="Events produced across the whole period, not on any single day."
            categories={ranked.map((entry) => entry.key)}
            categoryHeading="Device"
            series={{ key: 'events', label: 'Events', values: ranked.map((entry) => entry.value) }}
          />
        )
      }

      const axis = days(data.activity)
      const activity = data.activity

      return (
        <div className="table-scroll">
          <table data-testid="activity-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Events</th>
                <th className="secondary">Trend</th>
                <th className="secondary">Findings</th>
                <th className="secondary">Failures</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((entry) => {
                const rows = activity.filter((row) => row.deviceName === entry.key)
                return (
                  <tr key={entry.key}>
                    <td>{entry.key}</td>
                    <td>{exactNumber(entry.value)}</td>
                    <td className="secondary">
                      <Sparkline values={trendFor(activity, entry.key, axis)} label={`${entry.key} daily events`} />
                    </td>
                    <td className="secondary">
                      {exactNumber(rows.reduce((sum, row) => sum + row.findingCount, 0))}
                    </td>
                    <td className="secondary">
                      {exactNumber(rows.reduce((sum, row) => sum + row.failureCount, 0))}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    }
  },

  {
    id: 'noisiest-rules',
    title: 'Rules that fired',
    description: 'Which rules matched most across the period.',
    charts: ['bar', 'donut', 'table'],
    defaultChart: 'bar',
    defaultSize: 'half',
    defaultVisible: true,
    render: (chart, data) => {
      if (data.detections === null) return <Empty>{UNREADABLE}</Empty>
      const ranked = noisiestRules(data.detections)
      const detections = data.detections
      if (ranked.length === 0) return <Empty>Nothing matched in this period.</Empty>

      if (chart === 'donut') {
        return (
          <Donut
            title="Rules that fired"
            description="Matches across the period. The whole is what these rules matched between them, not everything the fleet reported."
            slices={ranked.map((entry) => ({ label: entry.key, value: entry.value }))}
            totalLabel="matches"
          />
        )
      }

      if (chart === 'bar') {
        return (
          <BarChart
            title="Rules that fired"
            description="Total matches across the period. A rule at the top of this list is either finding a real problem repeatedly or is written too broadly."
            categories={ranked.map((entry) => entry.key)}
            categoryHeading="Rule"
            series={{ key: 'matches', label: 'Matches', values: ranked.map((entry) => entry.value) }}
          />
        )
      }

      return (
        <div className="table-scroll">
          <table data-testid="detection-table">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Matches</th>
                <th className="secondary">Devices</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((entry) => (
                <tr key={entry.key}>
                  <td>{entry.key}</td>
                  <td>{exactNumber(entry.value)}</td>
                  <td className="secondary">
                    {exactNumber(
                      Math.max(
                        0,
                        ...detections.filter((row) => row.ruleName === entry.key).map((row) => row.deviceCount)
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
  },

  {
    id: 'severity-mix',
    title: 'Severity mix',
    description: 'How the period divides by the worst severity each device reached.',
    charts: ['donut', 'bar', 'table'],
    defaultChart: 'donut',
    defaultSize: 'half',
    defaultVisible: false,
    render: (chart, data) => {
      if (data.activity === null) return <Empty>{UNREADABLE}</Empty>
      const mix = severityMix(data.activity)
      if (mix.length === 0) return <Empty>No activity in this period.</Empty>

      if (chart === 'table') {
        return (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Events</th>
                </tr>
              </thead>
              <tbody>
                {mix.map((entry) => (
                  <tr key={entry.key}>
                    <td>{entry.key}</td>
                    <td>{exactNumber(entry.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      if (chart === 'bar') {
        return (
          <BarChart
            title="Severity mix"
            description="Events grouped by the worst severity the reporting device reached that day."
            categories={mix.map((entry) => entry.key)}
            categoryHeading="Severity"
            series={{ key: 'events', label: 'Events', values: mix.map((entry) => entry.value) }}
          />
        )
      }

      return (
        <Donut
          title="Severity mix"
          description="Events grouped by the worst severity the reporting device reached that day. The whole is every event in the period."
          slices={mix.map((entry) => ({ label: entry.key, value: entry.value }))}
          totalLabel="events"
        />
      )
    }
  },

  {
    id: 'subnet-coverage',
    title: 'Policy coverage by subnet',
    description: 'Where the fleet is, and which parts of it nothing is watching.',
    charts: ['stacked', 'bar', 'table'],
    defaultChart: 'stacked',
    defaultSize: 'full',
    defaultVisible: false,
    render: (chart, data) => {
      const subnets = data.topology?.subnets ?? []
      if (subnets.length === 0) return <Empty>No topology yet. Refresh it once devices have checked in.</Empty>

      if (chart === 'stacked') {
        const split = coverage(subnets)
        return (
          <StackedBarChart
            title="Policy coverage by subnet"
            description="The machines with no policy at all are drawn against the baseline, because that is the number to act on: a subnet where nothing is deployed is either a gap or a decision."
            categories={split.subnets}
            labels={split.subnets}
            series={[
              { key: 'uncovered', label: 'No policy', values: split.uncovered },
              { key: 'covered', label: 'Carrying a policy', values: split.covered }
            ]}
          />
        )
      }

      if (chart === 'bar') {
        const split = coverage(subnets)
        return (
          <BarChart
            title="Machines with no policy, by subnet"
            description="Only the uncovered count, which is the number to act on. The stacked form shows it against the size of each subnet."
            categories={split.subnets}
            categoryHeading="Subnet"
            series={{ key: 'uncovered', label: 'No policy', values: split.uncovered }}
          />
        )
      }

      return <TopologyTable topology={data.topology} />
    }
  },

  {
    id: 'topology-table',
    title: 'Fleet topology',
    description: 'Every subnet, with when it was last heard from.',
    charts: ['table', 'bar'],
    defaultChart: 'table',
    defaultSize: 'full',
    sizes: ['full', 'half'],
    defaultVisible: true,
    render: (chart, data) => (
      <>
        <div className="toolbar" style={{ marginBottom: '0.6rem' }}>
          <span className="muted">
            {data.topology?.deviceCount ?? 0} device(s)
            {data.topology?.projectedUtc && ` · as of ${formatUtc(data.topology.projectedUtc)}`}
          </span>
          <button
            type="button"
            onClick={data.rebuildTopology}
            disabled={data.busy}
            style={{ marginLeft: 'auto' }}
            data-testid="rebuild-topology"
          >
            Refresh topology
          </button>
        </div>
        {chart === 'bar' ? (
          <BarChart
            title="Devices by subnet"
            description="How the fleet is distributed. The table beside it carries when each subnet was last heard from, which a bar cannot show."
            categories={(data.topology?.subnets ?? []).map((subnet) => subnet.subnet)}
            categoryHeading="Subnet"
            series={{
              key: 'devices',
              label: 'Devices',
              values: (data.topology?.subnets ?? []).map((subnet) => subnet.deviceCount)
            }}
          />
        ) : (
          <TopologyTable topology={data.topology} />
        )}
      </>
    )
  },

  {
    id: 'projections',
    title: 'Projections',
    description: 'When each read model was last rebuilt, and the button that does it.',
    charts: ['table'],
    defaultChart: 'table',
    defaultSize: 'full',
    sizes: ['full', 'half'],
    // Off by default now, and on the System screen as well.
    //
    // Rebuilding a projection is the documented repair for a count inflated by a redelivered message
    // (OPERATIONS.md), and a repair nobody can find is not a repair - but it is an operator's job
    // rather than a tenant administrator's, and the Board is where somebody goes to decide what to do
    // about their fleet, not to repair the console. It stays in the catalog because switching it
    // back on is a checkbox, and because a saved layout that already has it keeps it (D-347).
    defaultVisible: false,
    render: (_chart, data) => (
      <>
        <p className="muted" style={{ marginTop: 0 }}>
          Counts are accumulated as events arrive, so a redelivered message can inflate one. Rebuilding
          recomputes it from the event feed, which is the repair.
        </p>
        <div className="toolbar" data-testid="projection-state">
          {REBUILDABLE.map((projection) => {
            // The state row only exists once a projection has been rebuilt, so the buttons come from
            // the fixed list and the timestamp is looked up rather than the other way round.
            const state = data.projections.find((candidate) => candidate.projection === projection)
            return (
              <button
                key={projection}
                type="button"
                onClick={() => data.rebuild(projection)}
                disabled={data.busy}
                data-testid={`rebuild-${projection}`}
                title={state?.rebuiltUtc ? `Last rebuilt ${formatUtc(state.rebuiltUtc)}` : 'Never rebuilt'}
              >
                Rebuild {projection}
              </button>
            )
          })}
        </div>
      </>
    )
  }
]

function TopologyTable({ topology }: { topology: FleetTopology | null }) {
  if (!topology || topology.subnets.length === 0) {
    return <Empty>No topology yet. Refresh it once devices have checked in.</Empty>
  }

  return (
    <div className="table-scroll">
      <table data-testid="topology-table">
        <thead>
          <tr>
            <th>Subnet</th>
            <th>Devices</th>
            <th>No policy</th>
            <th className="secondary">Active</th>
            <th className="secondary">Last seen</th>
          </tr>
        </thead>
        <tbody>
          {topology.subnets.map((subnet) => (
            <tr key={subnet.subnet}>
              <td className="mono">{subnet.subnet}</td>
              <td>{subnet.deviceCount}</td>
              <td>
                {subnet.devicesWithNoPolicy > 0 ? (
                  <span className="pill sev-3">{subnet.devicesWithNoPolicy}</span>
                ) : (
                  <span className="muted">none</span>
                )}
              </td>
              <td className="secondary">{subnet.activeDevices}</td>
              <td className="muted secondary">{formatUtc(subnet.lastSeenUtc)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const WIDGET_BY_ID = new Map(WIDGETS.map((widget) => [widget.id, widget]))
